<template>
  <div class="row-col-select-multiple-field">
    <h5>
      {{ label }}
      <v-chip v-if="required" class="ma-2" x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </h5>
    <v-row>
      <v-col>
        <v-combobox
          v-if="!readonly"
          v-model="selectedItems"
          :items="sortedItems"
          item-text="name"
          item-value="id"
          multiple
          chips
          clearable
          return-object
          hide-selected
          outlined
          :rules="allRules"
          :menu-props="{ maxHeight: 400 }"
          @input="updateValue"
        />
        <v-text-field
          v-else
          :value="displayValue"
          readonly
          disabled
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RowColSelectMultipleField",
  props: {
    label: {
      type: String,
      required: true
    },
    dataSource: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    allowCustomValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      items: [],
      selectedItems: [],
      isMounted: false,
      lastEmitted: []
    };
  },
  computed: {
    sortedItems() {
      return [...this.items].sort((a, b) => {
        const nameA = this.normalizeLabel(a).toLowerCase();
        const nameB = this.normalizeLabel(b).toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    },
    allRules() {
      const rules = [];
      if (this.required && this.isMounted) {
        rules.push(value => {
          const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
          return hasValue || this.$t("forms.field.validation.required");
        });
      }
      return rules;
    },
    displayValue() {
      if (!Array.isArray(this.value) || !this.value.length) {
        return "-";
      }
      const labels = this.normalizeIncoming(this.value).map(item =>
        typeof item === "string"
          ? item
          : item.name ||
            item.title ||
            item.internal_name ||
            item.slug ||
            item.value ||
            ""
      );
      const filtered = labels.filter(label => label && label.toString().trim() !== "");
      return filtered.length ? filtered.join(", ") : "-";
    }
  },
  watch: {
    value: {
      handler(newValue) {
        this.syncSelectedItems(newValue);
      },
      immediate: true,
      deep: true
    },
    items() {
      this.syncSelectedItems(this.value);
    }
  },
  methods: {
    fetchItems() {
      const config = this.resolveEndpointConfig();
      if (!config) {
        // Fail silently if the endpoint is unknown
        return;
      }
      axios
        .get(`${this.$store.state.endpoint}/${config.endpoint}`)
        .then(response => {
          const mappedItems = response.data.map(item => {
            const baseItem = typeof config.mapItem === "function"
              ? config.mapItem(item)
              : item;
            const id = this.resolveItemId(baseItem);
            const rawName = config.transform
              ? config.transform(baseItem)
              : (baseItem.name || baseItem.title || baseItem.internal_name || baseItem.slug || baseItem.value || id);
            return {
              ...baseItem,
              id,
              name: this.normalizeLabel(rawName)
            };
          });
          this.items = mappedItems.map(item => ({
            ...item,
            id: item.id !== undefined && item.id !== null && item.id !== ""
              ? item.id
              : this.resolveItemId(item),
            name: this.normalizeLabel(item)
          }));
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.syncSelectedItems(this.value);
        });
    },
    resolveEndpointConfig() {
      switch (this.dataSource) {
        case "person":
          return {
            endpoint: "personName",
            transform: item => `${item.preferred_name.given_name} ${item.preferred_name.family_name}`
          };
        case "organization":
          return {
            endpoint: "organization",
            transform: item => item.name
          };
        case "funder_program":
          return {
            endpoint: "funderProgram",
            transform: item => item.internal_name
          };
        case "license":
          return {
            endpoint: "software/licenses",
            mapItem: item => ({
              ...item,
              id: item.value,
              name: item.label || item.value
            })
          };
        case "dataset_license":
          return {
            endpoint: "dataset/licenses",
            mapItem: item => ({
              ...item,
              id: item.value,
              name: item.label || item.value
            })
          };
        case "platform":
          return {
            endpoint: "software/platforms",
            mapItem: item => ({
              ...item,
              id: item.value,
              name: item.label || item.value
            })
          };
        case "project":
          return {
            endpoint: "project",
            transform: item => {
              const title = item.title && typeof item.title === "object"
                ? item.title.gl || item.title.es || item.title.en
                : item.title;
              const acronym = typeof item.acronym === "string" ? item.acronym.trim() : "";
              const projectLabel = [acronym, title].filter(Boolean).join(": ");
              return projectLabel || item.name || item.slug;
            }
          };
        case "publication":
          return {
            endpoint: "publication",
            transform: item => item.title || item.name || item.slug
          };
        case "research_area":
          return {
            endpoint: "researchArea",
            transform: item => {
              if (item && item.name && typeof item.name === "object") {
                return item.name.en || item.name.gl || item.name.es || item.name;
              }
              return item && item.name ? item.name : item.slug;
            }
          };
        case "software":
          return {
            endpoint: "software",
            transform: item => item.name || item.title || item.slug
          };
        case "dataset":
          return {
            endpoint: "dataset",
            transform: item => item.title || item.name || item.slug
          };
        default:
          console.warn(`Unknown data source '${this.dataSource}' for RowColSelectMultipleField`);
          return null;
      }
    },
    extractId(value) {
      if (value === null || value === undefined) {
        return null;
      }
      if (typeof value === "object") {
        if (value.id !== undefined && value.id !== null) {
          return value.id;
        }
        if (value.value !== undefined && value.value !== null) {
          return value.value;
        }
        if (typeof value.slug === "string" && value.slug.trim() !== "") {
          return value.slug;
        }
      }
      return value;
    },
    resolveItemId(item) {
      if (!item || typeof item !== "object") {
        return item;
      }
      if (item.id !== undefined && item.id !== null) {
        return item.id;
      }
      if (item.value !== undefined && item.value !== null) {
        return item.value;
      }
      if (typeof item.slug === "string" && item.slug.trim() !== "") {
        return item.slug;
      }
      if (item.code !== undefined && item.code !== null) {
        return item.code;
      }
      if (typeof item.name === "string" && item.name.trim() !== "") {
        return item.name;
      }
      return null;
    },
    normalizeLabel(raw) {
      if (raw === null || raw === undefined) {
        return "";
      }
      if (typeof raw === "string") {
        return raw;
      }
      if (typeof raw !== "object") {
        return String(raw);
      }
      if (Array.isArray(raw)) {
        return raw.join(", ");
      }
      const label =
        raw.name ||
        raw.title ||
        raw.internal_name ||
        raw.slug ||
        raw.value ||
        raw.label ||
        raw.id ||
        "";
      if (label && typeof label === "object") {
        const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "en";
        return label[locale] || label.en || label.gl || label.es || JSON.stringify(label);
      }
      return String(label);
    },
    normalizeIncoming(rawValue) {
      if (!Array.isArray(rawValue)) {
        return [];
      }
      return rawValue
        .map(entry => {
          if (typeof entry === "string") {
            const match = this.items.find(item => item.id === entry || item.name === entry);
            if (match) {
              return match;
            }
            return this.allowCustomValue ? entry : null;
          }
          if (typeof entry === "number") {
            const match = this.items.find(item => item.id === entry);
            return match || null;
          }
          if (entry && typeof entry === "object") {
            const id = this.extractId(entry);
            const match = this.items.find(item => item.id === id);
            if (match) {
              return match;
            }
            if (this.allowCustomValue) {
              const label =
                entry.name ||
                entry.title ||
                entry.internal_name ||
                entry.slug ||
                entry.value;
              return label || null;
            }
          }
          return null;
        })
        .filter(item => item !== null);
    },
    normalizeOutgoing(selected) {
      if (!Array.isArray(selected)) {
        return [];
      }
      const normalized = selected
        .map(entry => {
          if (typeof entry === "string") {
            if (this.allowCustomValue) {
              const trimmed = entry.trim();
              return trimmed === "" ? null : trimmed;
            }
            const match = this.items.find(item => item.name === entry);
            return match ? match.id : null;
          }
          if (typeof entry === "number") {
            return entry;
          }
          if (entry && typeof entry === "object") {
            if (entry.id !== undefined && entry.id !== null) {
              return entry.id;
            }
            if (this.allowCustomValue) {
              const label =
                entry.name ||
                entry.title ||
                entry.internal_name ||
                entry.slug ||
                entry.value;
              return label || null;
            }
          }
          return null;
        })
        .filter(item => item !== null && item !== undefined && item !== "");
      return Array.from(new Set(normalized));
    },
    syncSelectedItems(rawValue) {
      const normalized = this.normalizeIncoming(rawValue);
      const areSame =
        normalized.length === this.selectedItems.length &&
        normalized.every((item, index) => {
          const current = this.selectedItems[index];
          if (typeof item === "string" || typeof current === "string") {
            return item === current;
          }
          if (item && current && typeof item === "object" && typeof current === "object") {
            return item.id === current.id;
          }
          return item === current;
        });
      if (!areSame) {
        this.selectedItems = normalized;
      }
    },
    updateValue(newValue) {
      if (!this.isMounted) {
        return;
      }
      const normalized = this.normalizeOutgoing(newValue);
      const sameLength =
        normalized.length === this.lastEmitted.length &&
        normalized.every((item, index) => item === this.lastEmitted[index]);
      if (!sameLength) {
        this.lastEmitted = normalized;
        this.$emit("input", normalized);
      }
    }
  },
  mounted() {
    this.fetchItems();
    this.$nextTick(() => {
      this.isMounted = true;
    });
  }
};
</script>

<style scoped>
.row-col-select-multiple-field ::v-deep .v-chip {
  max-width: 300px;
  min-height: 32px;
}
.row-col-select-multiple-field ::v-deep .v-chip .v-chip__content {
  max-width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
